import { defineMessages, IntlShape } from 'react-intl';
import { SmartImportWarning } from './types';

const warningMessages = defineMessages({
  emptyHeader: {
    id: 'imports.warning.emptyHeader',
    defaultMessage: 'An empty column header was replaced automatically.',
  },
  duplicateHeader: {
    id: 'imports.warning.duplicateHeader',
    defaultMessage: 'A duplicate column header was renamed automatically.',
  },
  excludedColumns: {
    id: 'imports.warning.excludedColumns',
    defaultMessage:
      'Some unrecognized columns will remain private and will not be sent for interpretation.',
  },
  invalidLatitude: {
    id: 'imports.warning.invalidLatitude',
    defaultMessage: 'Latitude is outside the valid −90 to 90 range.',
  },
  invalidLongitude: {
    id: 'imports.warning.invalidLongitude',
    defaultMessage: 'Longitude is outside the valid −180 to 180 range.',
  },
  structuralRow: {
    id: 'imports.warning.structuralRow',
    defaultMessage: 'The row has an invalid structure.',
  },
  unrecognizedValue: {
    id: 'imports.warning.unrecognizedValue',
    defaultMessage: 'A value in “{field}” could not be recognized.',
  },
  utmZoneAssumed: {
    id: 'imports.warning.utmZoneAssumed',
    defaultMessage: 'The UTM zone was inferred automatically.',
  },
  unknown: {
    id: 'imports.warning.unknown',
    defaultMessage: 'The import contains a warning in “{field}”.',
  },
});

const fieldMessages = defineMessages({
  coordinates: {
    id: 'imports.warning.field.coordinates',
    defaultMessage: 'Coordinates',
  },
  description: {
    id: 'general.description',
    defaultMessage: 'Description',
  },
  era: { id: 'find.era', defaultMessage: 'Era' },
  evId: { id: 'find.evidenceId', defaultMessage: 'Evidence ID' },
  foundAt: { id: 'find.foundAt', defaultMessage: 'Found at' },
  latitude: { id: 'general.latitude', defaultMessage: 'Latitude' },
  localization: {
    id: 'find.localization',
    defaultMessage: 'Localization',
  },
  longitude: { id: 'general.longitude', defaultMessage: 'Longitude' },
  material: { id: 'find.material', defaultMessage: 'Material' },
  name: { id: 'general.name', defaultMessage: 'Name' },
  note: { id: 'general.note', defaultMessage: 'Note' },
  period: { id: 'find.era', defaultMessage: 'Era' },
  row: { id: 'imports.warning.field.row', defaultMessage: 'Row' },
  type: { id: 'find.type', defaultMessage: 'Type' },
});

const errorMessages = defineMessages({
  noDataRows: {
    id: 'imports.failure.noDataRows',
    defaultMessage: 'The file contains no data rows.',
  },
  tooManyRows: {
    id: 'imports.failure.tooManyRows',
    defaultMessage: 'The file contains too many rows.',
  },
  outputTokenLimit: {
    id: 'imports.failure.outputTokenLimit',
    defaultMessage: 'Interpretation exceeded the provider output limit.',
  },
  sourceUnavailable: {
    id: 'imports.failure.sourceUnavailable',
    defaultMessage: 'The temporary source file is no longer available.',
  },
  unknown: {
    id: 'imports.interpretation.failedUnexpectedly',
    defaultMessage: 'The import failed unexpectedly.',
  },
});

function formatWarningField(field: string, intl: IntlShape): string {
  const message = fieldMessages[field as keyof typeof fieldMessages];

  return message ? intl.formatMessage(message) : field;
}

export function formatImportWarning(
  warning: SmartImportWarning,
  intl: IntlShape
): string {
  switch (warning.code) {
    case 'EMPTY_HEADER_RENAMED':
      return intl.formatMessage(warningMessages.emptyHeader);
    case 'DUPLICATE_HEADER_RENAMED':
      return intl.formatMessage(warningMessages.duplicateHeader);
    case 'EXCLUDED_COLUMNS':
      return intl.formatMessage(warningMessages.excludedColumns);
    case 'INVALID_LATITUDE':
      return intl.formatMessage(warningMessages.invalidLatitude);
    case 'INVALID_LONGITUDE':
      return intl.formatMessage(warningMessages.invalidLongitude);
    case 'STRUCTURAL_ROW_ERROR':
      return intl.formatMessage(warningMessages.structuralRow);
    case 'UNRECOGNIZED_VALUE':
      return intl.formatMessage(warningMessages.unrecognizedValue, {
        field: formatWarningField(warning.field, intl),
      });
    case 'UTM_ZONE_ASSUMED':
      return intl.formatMessage(warningMessages.utmZoneAssumed);
    default:
      return intl.formatMessage(warningMessages.unknown, {
        field: formatWarningField(warning.field, intl),
      });
  }
}

export function formatImportFailure(
  error: { code: string; message: string } | null,
  intl: IntlShape
): string {
  switch (error?.code) {
    case 'NO_DATA_ROWS':
      return intl.formatMessage(errorMessages.noDataRows);
    case 'TOO_MANY_ROWS':
      return intl.formatMessage(errorMessages.tooManyRows);
    case 'OPENAI_OUTPUT_TOKEN_LIMIT':
      return intl.formatMessage(errorMessages.outputTokenLimit);
    case 'ENOENT':
      return intl.formatMessage(errorMessages.sourceUnavailable);
    default:
      return intl.formatMessage(errorMessages.unknown);
  }
}
